/**
 * Content Studio
 *
 * Main component for AI-powered social media content generation.
 * Wizard-style UI: URL Input → Product Preview → Config → Generate → Results
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/v2/ui';
import {
  useExtractProduct,
  useExtractProductManual,
  useGenerateContent,
  useFormats,
  useStyles,
} from './hooks';
import {
  UrlInputSection,
  ProductPreview,
  FormatSelector,
  StyleSelector,
  OverlayConfig,
  GenerationResults,
  ManualUploadDialog,
  type ManualUploadData,
} from './components';
import type {
  ExtractedProduct,
  GeneratedImage,
  GeneratedCaption,
  OverlayConfig as OverlayConfigType,
} from '@/lib/api/content-studio';

type WizardStep = 'input' | 'config' | 'results';

const DEFAULT_OVERLAY: OverlayConfigType = {
  // Price
  show_price: true,

  // Caption & Hashtags
  show_caption: false,  // Default: deaktiviert
  show_hashtags: false, // Default: deaktiviert

  // CTA
  show_cta: true,
  cta_mode: 'auto',
  cta_text: '',

  // USP
  show_usp: true,  // Default: aktiviert
  usp_mode: 'auto',
  usp_texts: [],
  usp_count: 2,
};

export function ContentStudio() {
  const { t } = useTranslation();
  // Wizard state
  const [step, setStep] = useState<WizardStep>('input');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [product, setProduct] = useState<ExtractedProduct | null>(null);

  // Config state
  const [selectedFormats, setSelectedFormats] = useState<string[]>([
    'instagram_feed',
    'instagram_story',
  ]);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([
    'emotional',
    'factual',
  ]);
  const [overlayConfig, setOverlayConfig] =
    useState<OverlayConfigType>(DEFAULT_OVERLAY);

  // Results state
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [generatedCaptions, setGeneratedCaptions] = useState<
    Record<string, GeneratedCaption>
  >({});

  // Manual upload state
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);

  // API hooks
  const { data: formatsData } = useFormats();
  const { data: stylesData } = useStyles();
  const extractMutation = useExtractProduct();
  const extractManualMutation = useExtractProductManual();
  const generateMutation = useGenerateContent();

  const formats = formatsData?.formats || [];
  const styles = stylesData?.styles || [];

  // Handlers
  const handleExtract = async (url: string) => {
    try {
      setExtractError(null);
      const result = await extractMutation.mutateAsync(url);
      if (result.success && result.product && result.session_id) {
        setProduct(result.product);
        setSessionId(result.session_id);
        setStep('config');
      } else {
        // Check if error is 403 or Cloudflare-related
        const error = result.error || '';
        setExtractError(error);
        if (error.includes('403') || error.includes('Cloudflare') || error.includes('blockiert')) {
          setShowManualUpload(true);
        }
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unbekannter Fehler';
      setExtractError(errorMsg);
      // Show manual upload dialog on network errors or 403
      if (errorMsg.includes('403') || errorMsg.includes('Network')) {
        setShowManualUpload(true);
      }
    }
  };

  const handleManualUpload = async (data: ManualUploadData) => {
    const result = await extractManualMutation.mutateAsync({
      title: data.title,
      description: data.description || null,
      price: data.price || null,
      currency: data.currency,
      imageFile: data.imageFile,
    });

    if (result.success && result.product && result.session_id) {
      setProduct(result.product);
      setSessionId(result.session_id);
      setStep('config');
      setShowManualUpload(false);
      setExtractError(null);
    }
  };

  const handleGenerate = async () => {
    if (!sessionId) return;

    const result = await generateMutation.mutateAsync({
      session_id: sessionId,
      formats: selectedFormats,
      styles: selectedStyles,
      overlay: overlayConfig,
    });

    if (result.success && result.images && result.captions) {
      setGeneratedImages(result.images);
      setGeneratedCaptions(result.captions);
      setStep('results');
    }
  };

  const handleReset = () => {
    setStep('input');
    setSessionId(null);
    setProduct(null);
    setGeneratedImages([]);
    setGeneratedCaptions({});
    setSelectedFormats(['instagram_feed', 'instagram_story']);
    setSelectedStyles(['emotional', 'factual']);
    setOverlayConfig(DEFAULT_OVERLAY);
    setShowManualUpload(false);
    setExtractError(null);
  };

  const toggleFormat = (formatId: string) => {
    setSelectedFormats((prev) =>
      prev.includes(formatId)
        ? prev.filter((f) => f !== formatId)
        : [...prev, formatId]
    );
  };

  const toggleStyle = (styleId: string) => {
    setSelectedStyles((prev) =>
      prev.includes(styleId)
        ? prev.filter((s) => s !== styleId)
        : [...prev, styleId]
    );
  };

  const canGenerate =
    selectedFormats.length > 0 && selectedStyles.length > 0 && product;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">AI Content Studio</h1>
            <p className="text-sm text-neutral-500">
              Erstelle Social-Media-Content aus Produkt-URLs
            </p>
          </div>
        </div>

        {step !== 'input' && (
          <Button variant="outline" onClick={handleReset}>
            Neu starten
          </Button>
        )}
      </div>

      {/* Step: Input */}
      {step === 'input' && (
        <>
          <UrlInputSection
            onExtract={handleExtract}
            isLoading={extractMutation.isPending}
            error={extractError}
            onManualUpload={() => setShowManualUpload(true)}
          />

          {/* Manual Upload Dialog */}
          <ManualUploadDialog
            open={showManualUpload}
            onClose={() => setShowManualUpload(false)}
            onUpload={handleManualUpload}
            isLoading={extractManualMutation.isPending}
          />
        </>
      )}

      {/* Step: Config */}
      {step === 'config' && product && (
        <div className="space-y-6">
          <ProductPreview product={product} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <FormatSelector
              formats={formats}
              selectedFormats={selectedFormats}
              onToggle={toggleFormat}
            />
            <StyleSelector
              styles={styles}
              selectedStyles={selectedStyles}
              onToggle={toggleStyle}
            />
          </div>

          <OverlayConfig config={overlayConfig} onChange={setOverlayConfig} />

          <div className="flex justify-end">
            <Button
              size="lg"
              disabled={!canGenerate || generateMutation.isPending}
              onClick={handleGenerate}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {t('common.generating')}
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Content generieren
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step: Results */}
      {step === 'results' && (
        <GenerationResults
          images={generatedImages}
          captions={generatedCaptions}
        />
      )}
    </div>
  );
}
